import {useQuery} from '@tanstack/react-query';
import axios from 'axios';
import {Check, UserRound} from 'lucide-react';
import {useActiveWorkspace} from '@api/workspaces';
import {useAuth, writeStoredUser, type CurrentUser} from '@shared/api/auth';
import {cn} from '@lib/utils';
import {Button} from '@ui/shadcn/button';

/** Персона демо-стенда: пользователь воркспейса + подпись роли. */
interface DemoPersona extends CurrentUser {
  roleTitle: string;
}

const fetchPersonas = async (workspaceSid: string): Promise<DemoPersona[]> => {
  const {data} = await axios.get<DemoPersona[]>(
    `${import.meta.env.VITE_MEEREC_BASE ?? '/api/user/meerec/v1'}/__demo/personas`,
    {params: {workspaceSid}},
  );
  return data;
};

/**
 * Демо-стенд: «Смотреть как» — переключение между участниками воркспейса
 * без авторизации. Выбранный пользователь пишется в localStorage (его sid
 * уходит заголовком `X-Tests-User-Sid`), затем страница перезагружается,
 * чтобы сбросить кэши запросов и права.
 */
export const DemoPersonaSwitcher = ({onSwitched}: {onSwitched?: () => void}) => {
  const {currentUser} = useAuth();
  const {activeId} = useActiveWorkspace();
  const {data: personas = []} = useQuery({
    queryKey: ['demo', 'personas', activeId],
    queryFn: () => fetchPersonas(activeId as string),
    enabled: Boolean(activeId),
    staleTime: 60_000,
  });

  if (personas.length < 2) return null;

  const switchTo = (persona: DemoPersona) => {
    if (persona.sid === currentUser?.sid) return;
    const {roleTitle: _roleTitle, ...user} = persona;
    writeStoredUser(user);
    onSwitched?.();
    window.location.assign(`${import.meta.env.BASE_URL}dashboard`);
  };

  return (
    <div className='px-3 py-2.5'>
      <p className='text-[11px] font-medium text-[var(--color-muted-foreground)] mb-1.5'>
        Демо: смотреть как
      </p>
      <div className='max-h-[220px] overflow-y-auto thin-scroll -mx-1'>
        {personas.map((p) => {
          const active = p.sid === currentUser?.sid;
          return (
            <Button
              key={p.sid}
              type='button'
              variant='ghost'
              onClick={() => switchTo(p)}
              className={cn(
                'w-full justify-start gap-2 px-1 h-auto py-1.5 text-xs font-normal',
                active && 'bg-[var(--color-primary-soft)]',
              )}
            >
              <UserRound size={14} className='shrink-0 text-[var(--color-secondary)]' />
              <span className='flex-1 min-w-0 text-left'>
                <span className='block truncate text-[var(--color-foreground)]'>
                  {p.displayName ?? p.email}
                </span>
                <span className='block truncate text-[10px] text-[var(--color-muted-foreground)]'>
                  {p.roleTitle}
                </span>
              </span>
              {active && <Check size={14} className='shrink-0 text-[var(--color-primary)]' />}
            </Button>
          );
        })}
      </div>
    </div>
  );
};
